import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dataService } from "./data.js";
import { money, monthLabel, titleCase } from "./format.js";
import { Icon, ChevronDown, MetricSkeleton, TableSkeleton, ErrorPanel, SiteFooter } from "./components.jsx";
import { Overview, Explore, About, Cities, Pricing, Welcome, Rankings, City, County, Watchlist, SignIn, Account, Admin } from "./screens.jsx";
import Profile from "./Profile.jsx";
import { COUNTY_BY_SLUG } from "./tx-counties.js";
import {
  SITE_NAME, slugify, deslug, parseRoute,
  homePath, explorePath, aboutPath, mapPath, citiesPath, pricingPath, signinPath, accountPath, adminPath, rankingsPath, watchlistPath, countyPath, cityPath, bizPath,
  setHead, headFor,
} from "./slug.js";
import { useAccount, setWatchlist as syncWatchlist, ACCOUNTS_ENABLED, FREE_WATCHLIST_MAX } from "./plan.js";

/* Lazy: the map carries ~15KB gz of county geometry other pages don't need. */
const MapView = lazy(() => import("./MapView.jsx"));

const NAV = [
  { items: [
    { key: "overview", label: "Overview" },
    { key: "map", label: "Map" },
    { key: "cities", label: "Cities" },
    { key: "explore", label: "Explore" },
    { key: "rankings", label: "Rankings" },
    { key: "watch", label: "Watchlist" },
    { key: "pricing", label: "Pricing" },
  ]},
];

/* route.view → rail highlight key */
const NAV_FOR_VIEW = { overview: "overview", map: "map", cities: "cities", explore: "explore", rankings: "rankings", watchlist: "watch", pricing: "pricing", signin: "account", account: "account", admin: "account" };

export default function App() {
  const [months, setMonths] = useState(null);
  const [period, setPeriod] = useState(null);

  const [route, setRoute] = useState(() => parseRoute());
  const [selId, setSelId] = useState(null);
  const [selBiz, setSelBiz] = useState(null);      // last-known business object (name/city/county)
  const [resolveErr, setResolveErr] = useState(null);
  const [navOpen, setNavOpen] = useState(false);

  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { user: account, plan: accountPlan } = useAccount();
  const [watchlist, setWatchlist] = useState(() => new Set(account?.watchlist || []));
  useEffect(() => { setWatchlist(new Set(account?.watchlist || [])); }, [account?.watchlist?.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const [followedMkts, setFollowedMkts] = useState(() => new Set());

  /* ---- overview snapshot (mount + period change) --------------------------- */
  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    (months ? Promise.resolve(months) : dataService.months())
      .then((m) => {
        if (!live) return;
        if (!months) setMonths(m);
        const p = period || m.months[m.months.length - 2] || m.latest;
        if (!period) setPeriod(p);
        return dataService.snapshot(p, { limit: 500 });
      })
      .then((d) => { if (live && d) { setSnap(d); setLoading(false); } })
      .catch((e) => { if (live) { setError(e.message); setLoading(false); } });
    return () => { live = false; };
  }, [period, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- browser back/forward ---------------------------------------------- */
  useEffect(() => {
    const onPop = () => setRoute(parseRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((path, nextRoute) => {
    if (path !== window.location.pathname) window.history.pushState({}, "", path);
    setRoute(nextRoute);
    setNavOpen(false);
    window.scrollTo(0, 0);
  }, []);

  /* city → its dominant county, from the overview snapshot (metros) */
  const cityCounty = useMemo(() => {
    const m = new Map();
    (snap?.businesses || []).forEach((b) => { if (!m.has(b.city?.toUpperCase())) m.set(b.city?.toUpperCase(), b.county); });
    return m;
  }, [snap]);

  /* ---- navigation intents ------------------------------------------------- */
  const openProfile = useCallback((b) => {
    setSelBiz(b); setSelId(b.permit); setResolveErr(null);
    navigate(bizPath(b), { view: "business", county: slugify(b.county), city: slugify(b.city), bar: slugify(b.name) });
  }, [navigate]);

  const openCounty = useCallback((countyName) => {
    navigate(countyPath(countyName), { view: "county", county: slugify(countyName) });
  }, [navigate]);

  const openCity = useCallback((cityName, countyName) => {
    const cty = countyName || cityCounty.get(cityName?.toUpperCase());
    if (cty) {
      navigate(cityPath(cty, cityName), { view: "city", county: slugify(cty), city: slugify(cityName) });
      return;
    }
    /* Unknown county (city not in the overview snapshot): resolve its dominant
     * county from a quick city query, then route. */
    dataService.snapshot(period, { city: cityName, limit: 50 })
      .then((d) => {
        const c = d.businesses[0]?.county || "Texas";
        navigate(cityPath(c, cityName), { view: "city", county: slugify(c), city: slugify(cityName) });
      })
      .catch(() => navigate(cityPath("Texas", cityName), { view: "city", county: "texas", city: slugify(cityName) }));
  }, [navigate, cityCounty, period]);

  const goOverview = useCallback(() => navigate(homePath(), { view: "overview" }), [navigate]);
  const goExplore = useCallback(() => navigate(explorePath(), { view: "explore" }), [navigate]);
  const goRankings = useCallback(() => navigate(rankingsPath(), { view: "rankings" }), [navigate]);
  const goAbout = useCallback(() => navigate(aboutPath(), { view: "about" }), [navigate]);
  const goMap = useCallback(() => navigate(mapPath(), { view: "map" }), [navigate]);
  const goCities = useCallback(() => navigate(citiesPath(), { view: "cities" }), [navigate]);
  const goPricing = useCallback(() => navigate(pricingPath(), { view: "pricing" }), [navigate]);
  const goWatch = useCallback(() => navigate(watchlistPath(), { view: "watchlist" }), [navigate]);
  const goAccount = useCallback(() => navigate(accountPath(), { view: "account" }), [navigate]);
  const goAdmin = useCallback(() => navigate(adminPath(), { view: "admin" }), [navigate]);
  /* Sign-in with a return path; SignIn navigates back via goto(next). */
  const goSignIn = useCallback((next) => {
    const q = next ? `?next=${encodeURIComponent(next)}` : "";
    if (window.location.pathname + window.location.search !== signinPath() + q) window.history.pushState({}, "", signinPath() + q);
    setRoute({ view: "signin" }); setNavOpen(false); window.scrollTo(0, 0);
  }, []);
  const goto = useCallback((path) => {
    const p = path && path.startsWith("/") ? path : homePath();
    window.history.pushState({}, "", p);
    setRoute(parseRoute(p)); setNavOpen(false); window.scrollTo(0, 0);
  }, []);

  const onNav = useCallback((key) => {
    if (key === "overview") goOverview();
    else if (key === "explore") goExplore();
    else if (key === "rankings") goRankings();
    else if (key === "about") goAbout();
    else if (key === "map") goMap();
    else if (key === "cities") goCities();
    else if (key === "pricing") goPricing();
    else if (key === "watch") goWatch();
    else if (key === "account") (account ? goAccount() : goSignIn(""));
  }, [goOverview, goExplore, goRankings, goAbout, goMap, goCities, goPricing, goWatch, goAccount, goSignIn, account]);

  /* ---- resolve a business deep-link (slug → permit) ----------------------- */
  useEffect(() => {
    if (route.view !== "business" || !period) return;
    if (selBiz && selId && slugify(selBiz.name) === route.bar && slugify(selBiz.city) === route.city) return;
    let live = true;
    setResolveErr(null); setSelId(null);
    dataService.snapshot(period, { city: deslug(route.city), limit: 400 })
      .then((d) => {
        if (!live) return;
        const match = d.businesses.find((b) => slugify(b.name) === route.bar);
        if (match) { setSelBiz(match); setSelId(match.permit); }
        else setResolveErr(`We couldn’t find “${deslug(route.bar)}” in ${titleCase(deslug(route.city))}.`);
      })
      .catch((e) => live && setResolveErr(e.message));
    return () => { live = false; };
  }, [route, period]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- document <head> per route ----------------------------------------- */
  useEffect(() => {
    const countyName = COUNTY_BY_SLUG[route.county] || titleCase(deslug(route.county || ""));
    let d = {};
    if (route.view === "county") d = { county: countyName };
    else if (route.view === "city") d = { city: titleCase(selBiz?.city && slugify(selBiz.city) === route.city ? selBiz.city : deslug(route.city)) };
    else if (route.view === "business") d = { name: titleCase(selBiz?.name || deslug(route.bar)), city: titleCase(selBiz?.city || deslug(route.city)), county: countyName };
    const h = headFor(route.view, d);
    setHead({ ...h, path: window.location.pathname, view: route.view, data: d });
  }, [route, selBiz]);

  /* ---- watchlist / follow ------------------------------------------------- */
  const toggleWatch = useCallback((permit) => {
    if (ACCOUNTS_ENABLED && !account) { goSignIn(window.location.pathname); return; }   // watchlist needs an account
    const n = new Set(watchlist);
    if (n.has(permit)) n.delete(permit);
    else if (accountPlan !== "pro" && n.size >= FREE_WATCHLIST_MAX) { goPricing(); return; }   // free cap reached
    else n.add(permit);
    const prev = watchlist;
    setWatchlist(n);                                        // optimistic
    if (ACCOUNTS_ENABLED) syncWatchlist([...n]).catch(() => setWatchlist(prev));
  }, [account, accountPlan, watchlist, goPricing, goSignIn]);
  const toggleFollowMkt = useCallback((city) => {
    setFollowedMkts((s) => { const n = new Set(s); n.has(city) ? n.delete(city) : n.add(city); return n; });
  }, []);

  const navKey = NAV_FOR_VIEW[route.view] || "overview";

  const railNav = (
    <>
      <a className="wordmark" href={homePath()} onClick={(e) => { e.preventDefault(); goOverview(); }}>{SITE_NAME}</a>
      {NAV.map((g, gi) => (
        <div className="nav-group" key={gi}>
          {g.items.map((it) => (
            <button key={it.key} className={`nav-item ${navKey === it.key ? "active" : ""}`} onClick={() => onNav(it.key)}>
              <span className="dot" aria-hidden="true" /> {it.label}
            </button>
          ))}
        </div>
      ))}
      {ACCOUNTS_ENABLED && (
        <div className="nav-group nav-account">
          <button className={`nav-item ${navKey === "account" ? "active" : ""}`} onClick={() => onNav("account")}>
            <span className="dot" aria-hidden="true" /> {account ? "Account" : "Sign in"}
          </button>
          {account && <span className="nav-sub">{account.email}{accountPlan === "pro" ? " · PRO" : ""}</span>}
        </div>
      )}
    </>
  );

  const body = () => {
    if (route.view === "about") return <About />;                      // static, no snapshot needed
    if (route.view === "pricing") return <Pricing onSignIn={goSignIn} onAccount={goAccount} />;
    if (route.view === "welcome") return <Welcome onExplore={goOverview} onAccount={goAccount} />;
    if (route.view === "signin") return <SignIn onDone={(next) => goto(next || accountPath())} />;
    if (route.view === "account") return <Account onSignIn={goSignIn} onPricing={goPricing} onWatch={goWatch} onAdmin={goAdmin} onSignedOut={goOverview} />;
    if (route.view === "admin") return <Admin onSignIn={goSignIn} onHome={goOverview} />;
    if (error) return <div className="container"><ErrorPanel message={error} onRetry={() => setReloadKey((k) => k + 1)} /></div>;
    if (loading || !snap) {
      return (
        <div className="container" aria-busy="true">
          <div style={{ marginBottom: 26 }}>
            <span className="skel" style={{ width: 220, height: 11, display: "inline-block", marginBottom: 14 }} />
            <div className="skel" style={{ width: 420, maxWidth: "80%", height: 32, marginBottom: 12 }} />
            <div className="skel" style={{ width: 520, maxWidth: "95%", height: 13 }} />
          </div>
          <MetricSkeleton />
          <div style={{ height: 22 }} />
          <TableSkeleton />
        </div>
      );
    }
    switch (route.view) {
      case "business":
        if (resolveErr) return <div className="container"><div className="crumbs-inline"><button className="quiet-btn" onClick={goOverview}>← Home</button></div><ErrorPanel message={resolveErr} /></div>;
        if (!selId) return <div className="container" aria-busy="true"><Skel3 /></div>;
        return (
          <Profile
            permit={selId} snap={snap}
            onBack={goOverview} onCity={openCity} onCounty={openCounty} onOpen={openProfile}
            watchlist={watchlist} onToggleWatch={toggleWatch}
            months={months} period={period} onPeriod={setPeriod} onUpgrade={goPricing}
          />
        );
      case "explore":
        return <Explore snap={snap} onOpen={openProfile} onCity={openCity} onCounty={openCounty} months={months} period={period} onPeriod={setPeriod} onUpgrade={goPricing} />;
      case "map":
        return (
          <Suspense fallback={<div className="container" aria-busy="true"><TableSkeleton /></div>}>
            <MapView period={period} months={months} onPeriod={setPeriod} onCounty={openCounty} />
          </Suspense>
        );
      case "cities":
        return <Cities period={period} months={months} onPeriod={setPeriod} onCity={openCity} />;
      case "rankings":
        return <Rankings snap={snap} onOpen={openProfile} onCity={openCity} onCounty={openCounty} />;
      case "county":
        return <County county={COUNTY_BY_SLUG[route.county] || titleCase(deslug(route.county))} period={period} onOpen={openProfile} onCity={openCity} />;
      case "city":
        return (
          <City
            snap={snap} city={deslug(route.city)} period={period}
            onOpen={openProfile} onCounty={openCounty}
            following={followedMkts.has(deslug(route.city).toUpperCase())} onFollow={() => toggleFollowMkt(deslug(route.city).toUpperCase())}
          />
        );
      case "watchlist":
        return <Watchlist snap={snap} watchlist={watchlist} onOpen={openProfile} onRemove={toggleWatch} onExplore={goExplore} onSignIn={goSignIn} onPricing={goPricing} />;
      default:
        return <Overview snap={snap} onOpen={openProfile} onCity={openCity} onCounty={openCounty} months={months} period={period} onPeriod={setPeriod} />;
    }
  };

  return (
    <div className="shell">
      <nav className="rail" aria-label="Primary">{railNav}</nav>

      <div className="main-col">
        <div className="topstick">
          <div className="mobile-bar">
            <a className="wordmark" href={homePath()} onClick={(e) => { e.preventDefault(); goOverview(); }}>{SITE_NAME}</a>
            <button className="menu-btn" aria-label="Open navigation menu" onClick={() => setNavOpen(true)}>
              <span className="menu-ico"><Icon name="menu" stroke /></span>
            </button>
          </div>

          <header className="topbar">
            <SearchBox period={period} onOpen={openProfile} />
            <div className="topbar-right">
              <span className="period-btn">
                <span className="eyebrow">Period</span>
                <span className="val">{monthLabel(period)}</span>
                <ChevronDown />
                <select aria-label="Reporting period" value={period || ""} onChange={(e) => setPeriod(e.target.value)}>
                  {(months?.months ? [...months.months].reverse() : period ? [period] : []).map((m) => (
                    <option key={m} value={m}>{monthLabel(m)}{m === months?.latest ? " · partial" : ""}</option>
                  ))}
                </select>
              </span>
            </div>
          </header>
        </div>
        {navOpen && (
          <div className="drawer" onClick={(e) => { if (e.target === e.currentTarget) setNavOpen(false); }}>
            <nav className="drawer-panel" aria-label="Primary">{railNav}</nav>
          </div>
        )}

        <main className="content">{body()}</main>
        <SiteFooter onNav={onNav} />
      </div>
    </div>
  );
}

function Skel3() {
  return (
    <>
      <span className="skel" style={{ width: 260, height: 13, display: "inline-block", marginBottom: 26 }} />
      <div className="skel" style={{ width: 420, maxWidth: "80%", height: 34, marginBottom: 12 }} />
      <div className="skel" style={{ width: 280, height: 54, marginBottom: 24 }} />
      <div className="panel panel-pad"><span className="skel" style={{ width: "100%", height: 230, display: "block" }} /></div>
    </>
  );
}

/* ------------------------------------------------------- global search box */
function SearchBox({ period, onOpen }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [hot, setHot] = useState(0);
  const boxRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!q.trim() || !period) { setResults(null); return; }
    const id = ++seq.current;
    const t = setTimeout(() => {
      dataService.search(q.trim(), period)
        .then((d) => { if (seq.current === id) { setResults(d.results); setHot(0); } })
        .catch(() => { if (seq.current === id) setResults([]); });
    }, 220);
    return () => clearTimeout(t);
  }, [q, period]);

  useEffect(() => {
    const close = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  function pick(r) { onOpen(r); setOpen(false); setQ(""); setResults(null); }

  function onKey(e) {
    if (!results?.length) { if (e.key === "Escape") setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHot((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHot((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[hot]); }
    else if (e.key === "Escape") setOpen(false);
  }

  return (
    <div className="search-wrap" ref={boxRef}>
      <span className="search-glyph"><Icon name="search" /></span>
      <input
        className="search-input"
        placeholder="Search bars, cities, counties, ZIP, or permit #"
        aria-label="Search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      {open && q.trim() && (
        <div className="search-pop">
          {results === null ? (
            <div className="search-note">Searching…</div>
          ) : results.length === 0 ? (
            <div className="search-note">No matches for “{q.trim()}” in {monthLabel(period, true)}.</div>
          ) : results.map((r, i) => (
            <button key={r.id} className={`search-row ${i === hot ? "hot" : ""}`} onMouseEnter={() => setHot(i)} onClick={() => pick(r)}>
              <span>
                <span className="name">{titleCase(r.name)}</span>
                <div className="meta">{titleCase(r.city)} · {r.county} · {r.cat}</div>
              </span>
              <span className="amt">{money(r.total)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
